import type { SafetyCaseRecord, SafetyDashboardRow } from '../../services/reportsApi';
import { formatOverviewProblemTitle } from './safetyCaseOverviewPresenter';
import { resolveSafetyCaseId } from '../../utils/acceptanceHierarchyNavigation';
import { AFTER_SIGNED_SCAN_REVIEW_SCOPE } from '../incidents/afterSignedScanReview';
import {
  currentCaseStateLabel,
  dedupeSafetyCases,
  hasNewActivitySinceSignedScan,
  latestSignedSafetyScanLabel,
  originatingRiskPatternLabel,
  readCameraLabel,
  readSignedScanAt,
  readZoneLabel,
  safetyCaseOutcomeStateLabel,
  safetyCaseReviewStateLabel,
  safetyCaseCardTitle,
  safetyCaseDedupeKey,
} from './safetyCaseWorklist';
import { resolveDraftScanHandoffWindow } from '../acceptance/safetyScanReportContext';
import { safetyScanVerifyPath } from '../acceptance/scanVerifyNavigation';

export type SafetyScanHandoffParams = {
  orgId?: string | null;
  siteId?: string | null;
  safetyCaseId?: string | null;
  workflowId?: string | null;
  representativeIncidentId?: string | null;
  cameraId?: string | null;
  zoneId?: string | null;
  riskType?: string | null;
  riskPatternLabel?: string | null;
  scanScopeStart?: string | null;
  scanScopeEnd?: string | null;
  signedScanAt?: string | null;
  signedScanScopeStart?: string | null;
  signedScanScopeEnd?: string | null;
  latestSignedScanId?: string | null;
  newActivityWindowStart?: string | null;
  isUpdatedScan?: boolean;
};

export type UpdatedScanHandoffOptions = {
  scanScopeStart?: string | null;
  scanScopeEnd?: string | null;
  draftScanScopeStart?: string | null;
  draftScanScopeEnd?: string | null;
  isUpdatedScan?: boolean;
  signedScanAt?: string | null;
  signedScanScopeStart?: string | null;
  signedScanScopeEnd?: string | null;
  newActivityWindowStart?: string | null;
  latestSignedScanId?: string | null;
};

export type LinkedSafetyCaseResolution = {
  linkedCases: SafetyCaseRecord[];
  pendingSelection: SafetyCaseRecord[];
};

export type InheritedRepresentativeEvidenceReadiness = {
  state: 'ready' | 'needs_selection' | 'needs_review';
  label: string;
};

export type InheritedWorkspaceReadiness = {
  representativeEvidence: InheritedRepresentativeEvidenceReadiness;
  reviewComplete: boolean;
  actionOwnerAssigned: boolean;
  correctiveActionRecorded: boolean;
  measurementReady: boolean;
  measurementLabel: string;
};

function normalize(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function readParam(params: URLSearchParams, ...keys: string[]): string {
  for (const key of keys) {
    const value = String(params.get(key) || '').trim();
    if (value) return value;
  }
  return '';
}

function readHandoffFromReportPayload(payload: Record<string, unknown> | null | undefined): Partial<SafetyScanHandoffParams> {
  if (!payload || typeof payload !== 'object') return {};
  const handoff = payload.handoff;
  if (!handoff || typeof handoff !== 'object') return {};
  const raw = handoff as Record<string, unknown>;
  const parsed = handoff as Partial<SafetyScanHandoffParams>;
  return {
    ...parsed,
    safetyCaseId: String(parsed.safetyCaseId || raw.safety_case_id || '').trim() || parsed.safetyCaseId,
  };
}

function readHandoffFromWorkflowAnchor(anchor: Record<string, unknown> | null | undefined): Partial<SafetyScanHandoffParams> {
  if (!anchor || typeof anchor !== 'object') return {};
  const scope = anchor.scope && typeof anchor.scope === 'object'
    ? anchor.scope as Record<string, unknown>
    : {};
  const timeWindow = anchor.time_window && typeof anchor.time_window === 'object'
    ? anchor.time_window as Record<string, unknown>
    : {};
  const zones = Array.isArray(scope.zones) ? scope.zones : [];
  return {
    workflowId: String(anchor.workflow_id || '').trim() || undefined,
    representativeIncidentId: String(anchor.representative_incident_id || '').trim() || undefined,
    siteId: String(scope.site_id || '').trim() || undefined,
    cameraId: String(scope.camera_id || '').trim() || undefined,
    zoneId: String(scope.zone_id || zones[0] || '').trim() || undefined,
    riskType: String(scope.risk_type || '').trim() || undefined,
    riskPatternLabel: String(scope.risk_label || '').trim() || undefined,
    scanScopeStart: String(timeWindow.start || timeWindow.from || '').trim() || undefined,
    scanScopeEnd: String(timeWindow.end || timeWindow.to || '').trim() || undefined,
  };
}

function readSafetyCaseIdFromParams(
  params: URLSearchParams,
  persisted: Partial<SafetyScanHandoffParams>,
): string {
  const fromPersisted = String(persisted.safetyCaseId || '').trim();
  if (fromPersisted) return fromPersisted;
  return readParam(params, 'safety_case_id', 'finding_id');
}

export function readPersistedReportSafetyCaseId(
  report?: Record<string, unknown> | null,
): string {
  if (!report || typeof report !== 'object') return '';
  return String(report.safety_case_id || report.finding_id || '').trim();
}

export function liveSafetyCaseDisplayTitle(item?: unknown): string {
  if (!item || typeof item !== 'object') return '';
  const record = item as Record<string, unknown>;
  const explicit = String(
    record.safety_case_title
    || record.title
    || record.display_title
    || '',
  ).trim();
  if (explicit) return explicit;
  const scope = (record.scope && typeof record.scope === 'object')
    ? record.scope as Record<string, unknown>
    : {};
  const zones = Array.isArray(scope.zones) ? scope.zones : [];
  const place = String(
    record.zone_label
    || scope.zone_label
    || record.area_label
    || scope.area_label
    || zones[0]
    || '',
  ).trim();
  const riskType = String(scope.risk_type || record.risk_type || '').trim();
  if (!riskType && !place) return '';
  return formatOverviewProblemTitle({ riskType, place });
}

function mergeHandoffField<T>(...values: Array<T | null | undefined>): T | null {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === 'string' && !String(value).trim()) continue;
    return value;
  }
  return null;
}

export function parseSafetyScanHandoffFromSearchParams(
  params: URLSearchParams,
  reportPayload?: Record<string, unknown> | null,
  workflowAnchor?: Record<string, unknown> | null,
  persistedReport?: Record<string, unknown> | null,
): SafetyScanHandoffParams {
  const persisted = readHandoffFromReportPayload(reportPayload);
  const anchorHandoff = readHandoffFromWorkflowAnchor(workflowAnchor);
  const reviewScope = readParam(params, 'review_scope');
  const persistedReportCaseId = readPersistedReportSafetyCaseId(persistedReport);
  const urlSafetyCaseId = readSafetyCaseIdFromParams(params, persisted);
  const urlRepresentativeIncidentId = readParam(params, 'incident_id', 'representative_incident_id');
  return {
    orgId: mergeHandoffField(persisted.orgId, anchorHandoff.orgId, readParam(params, 'org_id')),
    siteId: mergeHandoffField(persisted.siteId, anchorHandoff.siteId, readParam(params, 'site_id')),
    safetyCaseId: mergeHandoffField(persistedReportCaseId, persisted.safetyCaseId, urlSafetyCaseId),
    workflowId: mergeHandoffField(persisted.workflowId, anchorHandoff.workflowId, readParam(params, 'workflow_id')),
    representativeIncidentId: mergeHandoffField(
      persisted.representativeIncidentId,
      anchorHandoff.representativeIncidentId,
      urlRepresentativeIncidentId,
    ),
    cameraId: mergeHandoffField(persisted.cameraId, anchorHandoff.cameraId, readParam(params, 'camera_id')),
    zoneId: mergeHandoffField(persisted.zoneId, anchorHandoff.zoneId, readParam(params, 'zone_id')),
    riskType: mergeHandoffField(persisted.riskType, anchorHandoff.riskType, readParam(params, 'policy_type', 'risk_type')),
    riskPatternLabel: mergeHandoffField(
      persisted.riskPatternLabel,
      anchorHandoff.riskPatternLabel,
      readParam(params, 'risk_pattern_label', 'policy_name'),
    ),
    scanScopeStart: mergeHandoffField(
      persisted.scanScopeStart,
      anchorHandoff.scanScopeStart,
      readParam(params, 'scan_scope_start', 'report_from'),
    ),
    scanScopeEnd: mergeHandoffField(
      persisted.scanScopeEnd,
      anchorHandoff.scanScopeEnd,
      readParam(params, 'scan_scope_end', 'report_to'),
    ),
    latestSignedScanId: mergeHandoffField(
      persisted.latestSignedScanId,
      readParam(params, 'latest_signed_scan_id'),
    ),
    signedScanAt: mergeHandoffField(
      persisted.signedScanAt,
      readParam(params, 'signed_scan_at'),
    ),
    signedScanScopeStart: mergeHandoffField(
      persisted.signedScanScopeStart,
      readParam(params, 'signed_scan_scope_start'),
    ),
    signedScanScopeEnd: mergeHandoffField(
      persisted.signedScanScopeEnd,
      readParam(params, 'signed_scan_scope_end'),
    ),
    newActivityWindowStart: mergeHandoffField(
      persisted.newActivityWindowStart,
      readParam(params, 'new_activity_window_start'),
    ),
    isUpdatedScan: persisted.isUpdatedScan
      || reviewScope === AFTER_SIGNED_SCAN_REVIEW_SCOPE
      || Boolean(persisted.latestSignedScanId || readParam(params, 'latest_signed_scan_id')),
  };
}

function resolveUpdatedScanScopeEnd(options?: { scanScopeEnd?: string | null }): string {
  return String(options?.scanScopeEnd || '').trim() || new Date().toISOString();
}

export function buildHandoffFromSafetyCaseRecord(
  item: SafetyCaseRecord,
  scope: { orgId?: string | null; siteId?: string | null },
  options?: UpdatedScanHandoffOptions,
): SafetyScanHandoffParams {
  const signedAt = String(options?.signedScanAt || readSignedScanAt(item) || '').trim() || null;
  const isUpdatedScan = options?.isUpdatedScan ?? hasNewActivitySinceSignedScan(item);
  const legacyWindowStart = String(item.time_window?.start || item.time_window?.from || '').trim() || null;
  const legacyWindowEnd = String(item.time_window?.end || item.time_window?.to || '').trim() || null;
  const snapshot = item.status_snapshot && typeof item.status_snapshot === 'object'
    ? item.status_snapshot as Record<string, unknown>
    : {};
  const hasPersistedDraft = Boolean(String(item.active_draft_report_id || '').trim());
  const draftWindow = resolveDraftScanHandoffWindow({
    hasPersistedDraft,
    queueWindow: {
      start: options?.draftScanScopeStart,
      end: options?.draftScanScopeEnd,
    },
    reportAnchorWindow: item.workflow_anchor?.time_window,
    caseProjectionWindow: {
      start: snapshot.latest_scan_scope_start || snapshot.report_scope_start,
      end: snapshot.latest_scan_scope_end || snapshot.report_scope_end,
    },
    caseSeedWindow: item.time_window,
  });
  const signedScopeStart = String(
    options?.signedScanScopeStart || legacyWindowStart || '',
  ).trim() || null;
  const signedScopeEnd = String(
    options?.signedScanScopeEnd || legacyWindowEnd || '',
  ).trim() || null;
  return {
    orgId: scope.orgId,
    siteId: String(item.site_id || scope.siteId || '').trim() || null,
    safetyCaseId: safetyCaseDedupeKey(item) || null,
    workflowId: String(item.workflow_id || '').trim() || null,
    representativeIncidentId: String(item.representative_incident_id || '').trim() || null,
    cameraId: readCameraLabel(item),
    zoneId: readZoneLabel(item) !== 'Not recorded' ? readZoneLabel(item) : null,
    riskType: String(item.risk_type || item.hazard_category || item.finding_type || '').trim() || null,
    riskPatternLabel: originatingRiskPatternLabel(item),
    scanScopeStart: hasPersistedDraft
      ? (draftWindow.start || null)
      : (isUpdatedScan
        ? (String(options?.scanScopeStart ?? signedAt ?? '').trim() || null)
        : (String(options?.scanScopeStart || legacyWindowStart || '').trim() || null)),
    scanScopeEnd: hasPersistedDraft
      ? (draftWindow.end || null)
      : (isUpdatedScan
        ? resolveUpdatedScanScopeEnd(options)
        : (String(options?.scanScopeEnd || legacyWindowEnd || '').trim() || null)),
    signedScanAt: isUpdatedScan ? signedAt : null,
    signedScanScopeStart: isUpdatedScan ? signedScopeStart : null,
    signedScanScopeEnd: isUpdatedScan ? signedScopeEnd : null,
    latestSignedScanId: String(
      options?.latestSignedScanId || item.latest_finalized_report_id || '',
    ).trim() || null,
    newActivityWindowStart: isUpdatedScan
      ? (String(options?.newActivityWindowStart || signedAt || '').trim() || null)
      : null,
    isUpdatedScan,
  };
}

export function buildProofPackageHandoffFromReviewSource(args: {
  reviewSource?: Record<string, unknown> | null;
  selectedIncident?: unknown;
  searchParams?: URLSearchParams | null;
  dashboardRow?: SafetyDashboardRow | null;
}): Pick<SafetyScanHandoffParams, 'workflowId' | 'safetyCaseId' | 'representativeIncidentId'> {
  const reviewSource = args.reviewSource || null;
  const selectedIncident = args.selectedIncident && typeof args.selectedIncident === 'object'
    ? args.selectedIncident as Record<string, unknown>
    : null;
  const searchParams = args.searchParams || null;
  const dashboardRow = args.dashboardRow || null;

  const representativeIncidentId = (() => {
    const fromPatternSource = reviewSource?.pattern_source && typeof reviewSource.pattern_source === 'object'
      ? String((reviewSource.pattern_source as Record<string, unknown>).representative_incident_id || '').trim()
      : '';
    return String(
      dashboardRow?.representative_incident_id
      || fromPatternSource
      || reviewSource?.review_anchor_incident_id
      || reviewSource?.linked_detailed_record_id
      || selectedIncident?.incident_id
      || searchParams?.get('incident_id')
      || '',
    ).trim() || null;
  })();

  const workflowId = String(
    dashboardRow?.workflow_id
    || searchParams?.get('workflow_id')
    || reviewSource?.group_key
    || selectedIncident?.group_key
    || '',
  ).trim() || null;

  const safetyCaseId = String(
    resolveSafetyCaseId(dashboardRow)
    || searchParams?.get('safety_case_id')
    || searchParams?.get('finding_id')
    || reviewSource?.safety_case_id
    || reviewSource?.finding_id
    || (selectedIncident?.safety_case && typeof selectedIncident.safety_case === 'object'
      ? resolveSafetyCaseId(selectedIncident.safety_case as { safety_case_id?: string; finding_id?: string })
      : '')
    || '',
  ).trim() || null;

  return {
    workflowId,
    safetyCaseId,
    representativeIncidentId,
  };
}

export function buildHandoffFromDashboardRow(
  row: SafetyDashboardRow,
  scope: { orgId?: string | null; siteId?: string | null },
  options?: UpdatedScanHandoffOptions,
): SafetyScanHandoffParams {
  const signedAt = String(
    options?.signedScanAt || row.report_finalized_at || row.latest_scan_signed_at || '',
  ).trim() || null;
  const isUpdatedScan = options?.isUpdatedScan ?? Boolean(row.has_new_activity_since_report || row.workflow_changed_since_signoff);
  const legacyWindowStart = String(row.report_scope_start || row.latest_scan_scope_start || '').trim() || null;
  const legacyWindowEnd = String(row.report_scope_end || row.latest_scan_scope_end || '').trim() || null;
  const signedScopeStart = String(
    options?.signedScanScopeStart || legacyWindowStart || '',
  ).trim() || null;
  const signedScopeEnd = String(
    options?.signedScanScopeEnd || legacyWindowEnd || '',
  ).trim() || null;
  return {
    orgId: scope.orgId,
    siteId: String(row.site_id || scope.siteId || '').trim() || null,
    safetyCaseId: String(row.safety_case_id || row.finding_id || '').trim() || null,
    workflowId: String(row.workflow_id || '').trim() || null,
    representativeIncidentId: String(row.representative_incident_id || '').trim() || null,
    cameraId: String(row.camera_id || row.camera_label || '').trim() || null,
    zoneId: String(row.zone_id || row.zone_label || row.area_label || '').trim() || null,
    riskType: String(row.risk_type || '').trim() || null,
    riskPatternLabel: String(row.risk_pattern_label || row.risk_label || '').trim() || null,
    scanScopeStart: isUpdatedScan
      ? (String(options?.scanScopeStart ?? signedAt ?? '').trim() || null)
      : (String(options?.scanScopeStart || legacyWindowStart || '').trim() || null),
    scanScopeEnd: isUpdatedScan
      ? resolveUpdatedScanScopeEnd(options)
      : (String(options?.scanScopeEnd || legacyWindowEnd || '').trim() || null),
    signedScanAt: isUpdatedScan ? signedAt : null,
    signedScanScopeStart: isUpdatedScan ? signedScopeStart : null,
    signedScanScopeEnd: isUpdatedScan ? signedScopeEnd : null,
    latestSignedScanId: String(
      options?.latestSignedScanId || row.latest_finalized_report_id || row.report_id || '',
    ).trim() || null,
    newActivityWindowStart: isUpdatedScan
      ? (String(options?.newActivityWindowStart || signedAt || '').trim() || null)
      : null,
    isUpdatedScan,
  };
}

export function buildSafetyScanWorkspacePath(
  handoff: SafetyScanHandoffParams,
  reportId?: string | null,
): string {
  const id = String(reportId || '').trim();
  if (!id) return '';
  return safetyScanVerifyPath(id, {
    orgId: handoff.orgId,
    siteId: handoff.siteId,
    safetyCaseId: handoff.safetyCaseId,
    incidentId: handoff.representativeIncidentId,
  });
}

export function buildRepeatSafetyScanPath(args: {
  sourceWorkflowId?: string | null;
  siteId?: string | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  cameraId?: string | null;
  cameraLabel?: string | null;
  zoneId?: string | null;
  zoneLabel?: string | null;
  riskType?: string | null;
  name?: string | null;
  safetyCaseId?: string | null;
}): string | null {
  const workflowId = String(args.sourceWorkflowId || '').trim();
  const siteId = String(args.siteId || '').trim();
  const windowStart = String(args.windowStart || '').trim();
  const windowEnd = String(args.windowEnd || '').trim();
  const cameraId = String(args.cameraId || '').trim();
  const cameraLabel = String(args.cameraLabel || '').trim();
  const zoneId = String(args.zoneId || '').trim();
  const zoneLabel = String(args.zoneLabel || '').trim();
  const riskType = String(args.riskType || '').trim();
  const name = String(args.name || '').trim();
  const safetyCaseId = String(args.safetyCaseId || '').trim();
  const startMs = Date.parse(windowStart);
  const endMs = Date.parse(windowEnd);
  if (
    !workflowId
    || !siteId
    || !riskType
    || (!cameraId && !zoneId)
    || !Number.isFinite(startMs)
    || !Number.isFinite(endMs)
    || endMs <= startMs
  ) {
    return null;
  }
  const windowSeconds = Math.round((endMs - startMs) / 1000);
  if (!Number.isSafeInteger(windowSeconds) || windowSeconds <= 0 || windowSeconds > 31_536_000) {
    return null;
  }
  const params = new URLSearchParams();
  params.set('repeat_scan', '1');
  params.set('handoff_version', '1');
  params.set('workflow_id', workflowId);
  params.set('site_id', siteId);
  params.set('window_seconds', String(windowSeconds));
  params.set('risk_type', riskType);
  if (name) params.set('name', name);
  if (cameraId) params.set('camera_id', cameraId);
  if (cameraLabel) params.set('camera_label', cameraLabel);
  if (zoneId) params.set('zone_id', zoneId);
  if (zoneLabel) params.set('zone_label', zoneLabel);
  if (safetyCaseId) params.set('safety_case_id', safetyCaseId);
  return `/acceptance/scan-definitions?${params.toString()}`;
}

export function buildCreateDraftPayloadFromHandoff(
  handoff: SafetyScanHandoffParams,
): {
  representative_incident_id?: string;
  scope?: Record<string, unknown>;
  time_window?: Record<string, unknown>;
  workflow_anchor?: Record<string, unknown>;
  report_payload?: Record<string, unknown>;
} {
  const scope = {
    site_id: handoff.siteId || undefined,
    camera_id: handoff.cameraId || undefined,
    zone_id: handoff.zoneId || undefined,
    zones: handoff.zoneId ? [handoff.zoneId] : undefined,
    risk_type: handoff.riskType || undefined,
    risk_label: handoff.riskPatternLabel || undefined,
  };
  const timeWindow = {
    start: handoff.scanScopeStart || undefined,
    end: handoff.scanScopeEnd || undefined,
    from: handoff.scanScopeStart || undefined,
    to: handoff.scanScopeEnd || undefined,
  };
  return {
    representative_incident_id: handoff.representativeIncidentId || undefined,
    scope,
    time_window: timeWindow,
    workflow_anchor: {
      workflow_id: handoff.workflowId || undefined,
      representative_incident_id: handoff.representativeIncidentId || undefined,
      scope,
      time_window: timeWindow,
    },
    report_payload: {
      source_model: handoff.isUpdatedScan ? 'safety_case_updated_scan' : 'safety_case_handoff',
      org_id: handoff.orgId || undefined,
      site_id: handoff.siteId || undefined,
      handoff,
    },
  };
}

function findCaseById(cases: SafetyCaseRecord[], caseId: string): SafetyCaseRecord | null {
  const normalizedId = String(caseId || '').trim();
  if (!normalizedId) return null;
  return cases.find((item) => safetyCaseDedupeKey(item) === normalizedId) || null;
}

function findCasesByWorkflow(cases: SafetyCaseRecord[], workflowId: string): SafetyCaseRecord[] {
  const normalizedWorkflowId = String(workflowId || '').trim();
  if (!normalizedWorkflowId) return [];
  return dedupeSafetyCases(cases.filter((item) => String(item.workflow_id || '').trim() === normalizedWorkflowId));
}

function findCasesByRepresentativeIncident(cases: SafetyCaseRecord[], incidentId: string): SafetyCaseRecord[] {
  const normalizedIncidentId = String(incidentId || '').trim();
  if (!normalizedIncidentId) return [];
  return dedupeSafetyCases(
    cases.filter((item) => String(item.representative_incident_id || '').trim() === normalizedIncidentId),
  );
}

export function resolveLinkedSafetyCases(args: {
  explicitCaseId?: string | null;
  explicitCase?: SafetyCaseRecord | null;
  workflowId?: string | null;
  representativeIncidentId?: string | null;
  siteCases: SafetyCaseRecord[];
}): LinkedSafetyCaseResolution {
  const explicitCaseId = String(args.explicitCaseId || '').trim();
  if (args.explicitCase) {
    return { linkedCases: [args.explicitCase], pendingSelection: [] };
  }
  if (explicitCaseId) {
    const matched = findCaseById(args.siteCases, explicitCaseId);
    if (matched) return { linkedCases: [matched], pendingSelection: [] };
  }

  const workflowMatches = findCasesByWorkflow(args.siteCases, String(args.workflowId || '').trim());
  if (workflowMatches.length === 1) {
    return { linkedCases: workflowMatches, pendingSelection: [] };
  }
  if (workflowMatches.length > 1) {
    return { linkedCases: [], pendingSelection: workflowMatches };
  }

  const incidentMatches = findCasesByRepresentativeIncident(
    args.siteCases,
    String(args.representativeIncidentId || '').trim(),
  );
  if (incidentMatches.length === 1) {
    return { linkedCases: incidentMatches, pendingSelection: [] };
  }
  if (incidentMatches.length > 1) {
    return { linkedCases: [], pendingSelection: incidentMatches };
  }

  return { linkedCases: [], pendingSelection: [] };
}

export async function loadLinkedSafetyCasesForHandoff(args: {
  explicitCaseId?: string | null;
  workflowId?: string | null;
  representativeIncidentId?: string | null;
  openSafetyCase: (safetyCaseId: string) => Promise<{
    safety_case?: SafetyCaseRecord | null;
    finding?: SafetyCaseRecord | null;
  }>;
  listSafetyCases: (opts: { limit: number }) => Promise<{ safety_cases?: SafetyCaseRecord[] | null }>;
}): Promise<LinkedSafetyCaseResolution> {
  const explicitCaseId = String(args.explicitCaseId || '').trim();
  let explicitCase: SafetyCaseRecord | null = null;
  if (explicitCaseId) {
    try {
      const opened = await args.openSafetyCase(explicitCaseId);
      explicitCase = opened.safety_case || opened.finding || null;
    } catch {
      explicitCase = null;
    }
  }

  let siteCases: SafetyCaseRecord[] = [];
  try {
    const listResponse = await args.listSafetyCases({ limit: 200 });
    siteCases = listResponse.safety_cases || [];
  } catch {
    if (explicitCase) {
      return { linkedCases: [explicitCase], pendingSelection: [] };
    }
    throw new Error('list_safety_cases_failed');
  }

  return resolveLinkedSafetyCases({
    explicitCaseId,
    explicitCase,
    workflowId: args.workflowId,
    representativeIncidentId: args.representativeIncidentId,
    siteCases,
  });
}

function reviewIsComplete(item: SafetyCaseRecord | null): boolean {
  if (!item) return false;
  if (item.review_status?.review_complete) return true;
  const status = normalize(item.review_status?.status);
  return status === 'reviewed' || status === 'complete' || status === 'completed';
}

function hasRepresentativeEvidence(item: SafetyCaseRecord | null, handoff: SafetyScanHandoffParams): boolean {
  return Boolean(
    String(item?.representative_incident_id || handoff.representativeIncidentId || handoff.safetyCaseId || '').trim(),
  );
}

export function deriveRepresentativeEvidenceReadiness(
  item: SafetyCaseRecord | null,
  handoff: SafetyScanHandoffParams,
): InheritedRepresentativeEvidenceReadiness {
  if (!hasRepresentativeEvidence(item, handoff)) {
    return { state: 'needs_selection', label: 'Needs selection' };
  }
  if (reviewIsComplete(item)) {
    return { state: 'ready', label: 'Ready' };
  }
  return { state: 'needs_review', label: 'Open Review' };
}

function responseOwnerAssigned(item: SafetyCaseRecord | null): boolean {
  if (!item) return false;
  if (item.has_followup_owner === false) return false;
  const owner = String(item.followup_owner || item.followup_team || '').trim();
  if (owner) return true;
  const snapshot = item.status_snapshot && typeof item.status_snapshot === 'object'
    ? item.status_snapshot as Record<string, unknown>
    : {};
  const ownership = snapshot.ownership && typeof snapshot.ownership === 'object'
    ? snapshot.ownership as Record<string, unknown>
    : {};
  if (ownership.has_followup_owner === false) return false;
  const snapshotOwner = String(
    ownership.followup_owner || ownership.followup_team || ownership.action_owner || '',
  ).trim();
  return Boolean(snapshotOwner);
}

function correctiveActionRecorded(item: SafetyCaseRecord | null): boolean {
  if (!item) return false;
  const status = normalize(item.corrective_response_status);
  if (['completed', 'validated', 'waived', 'not_required', 'action_completed'].includes(status)) {
    return true;
  }
  const recommendations = item.recommendations || [];
  return recommendations.some((recommendation) => {
    const state = normalize(recommendation.current_decision_state || recommendation.latest_decision?.decision_state);
    return ['accepted', 'modified', 'rejected', 'deferred'].includes(state);
  });
}

function measurementReadiness(item: SafetyCaseRecord | null): { ready: boolean; label: string } {
  if (!item) return { ready: false, label: 'Pending' };
  const measurementStatus = normalize(item.measurement_status);
  const claimStatus = normalize(item.claim_status);
  if (['completed', 'waived', 'accepted_insufficient'].includes(measurementStatus)) {
    return { ready: true, label: 'Measured' };
  }
  if (['not_claimed', 'not_claimable_yet', 'directional_only', 'observed_change_only'].includes(claimStatus)) {
    return { ready: true, label: 'Not claimed' };
  }
  if (measurementStatus === 'waived' || claimStatus === 'waived') {
    return { ready: true, label: 'Waived / not applicable' };
  }
  if (normalize(item.safety_case_status) === 'validated') {
    return { ready: true, label: 'Validated outcome recorded' };
  }
  return { ready: false, label: 'Pending' };
}

export function deriveInheritedWorkspaceReadiness(
  item: SafetyCaseRecord | null,
  handoff: SafetyScanHandoffParams,
): InheritedWorkspaceReadiness {
  const representativeEvidence = deriveRepresentativeEvidenceReadiness(item, handoff);
  const measurement = measurementReadiness(item);
  return {
    representativeEvidence,
    reviewComplete: representativeEvidence.state === 'ready',
    actionOwnerAssigned: responseOwnerAssigned(item),
    correctiveActionRecorded: correctiveActionRecorded(item),
    measurementReady: measurement.ready,
    measurementLabel: measurement.label,
  };
}

export function buildLinkedSafetyCaseContextLines(item: SafetyCaseRecord, handoff: SafetyScanHandoffParams): Array<{ label: string; value: string }> {
  const camera = readCameraLabel(item);
  const zone = readZoneLabel(item);
  const signedLabel = latestSignedSafetyScanLabel(item);
  const workflowId = String(item.workflow_id || handoff.workflowId || '').trim();
  const siteId = String(item.site_id || handoff.siteId || '').trim();
  const reviewState = safetyCaseReviewStateLabel(item);
  const outcomeState = safetyCaseOutcomeStateLabel(item);
  const lines = [
    { label: 'Safety Case title', value: liveSafetyCaseDisplayTitle(item) || safetyCaseCardTitle(item) },
    { label: 'Originating Risk Pattern', value: originatingRiskPatternLabel(item) },
    { label: 'Site', value: siteId || 'Not recorded' },
    { label: 'Camera / Zone', value: [camera ? `Camera ${camera}` : null, zone !== 'Not recorded' ? zone : null].filter(Boolean).join(' · ') || 'Not recorded' },
    { label: 'workflow_id', value: workflowId || 'Not recorded' },
    { label: 'Current case state', value: currentCaseStateLabel(item) },
    { label: 'Latest signed scan', value: signedLabel },
  ];
  if (reviewState) {
    lines.splice(6, 0, { label: 'Review state', value: reviewState });
  }
  if (outcomeState) {
    lines.splice(reviewState ? 7 : 6, 0, { label: 'Outcome state', value: outcomeState });
  }
  if (handoff.isUpdatedScan && handoff.newActivityWindowStart) {
    lines.push({
      label: 'New activity window',
      value: `${handoff.newActivityWindowStart} -> now`,
    });
  }
  return lines;
}
